import { useCallback, useEffect, useMemo, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { getEsignatureIntegrations } from '~/common/constants/integrations';
import { useErrorToast } from '~/common/errors/useErrorToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { Modal } from '~/eds/src/components/Modal/Modal';
import { StatusMessage } from '~/eds/src/components/StatusMessage/StatusMessage';
import { Text } from '~/eds/src/components/Text/Text';
import { EsignatureType } from '~/enums/EsignatureType';
import { api } from '~/redux/api/createApi';
import { TicketDocument } from '~/types/Ticket';

import { SortableDocumentList } from './SortableDocumentList';
import { SelectedTicket } from './types';

const messages = defineMessages({
  title: {
    id: 'EsignatureBundleModal.title',
    defaultMessage: 'Send to {providerName}',
    description: 'Modal title for sending documents to e-signature provider',
  },
  description: {
    id: 'EsignatureBundleModal.description',
    defaultMessage:
      "Documents are merged into one signature packet in the order shown. Drag to reorder. After the packet is created, documents can't be added or removed. To make changes, cancel the e-signature provider and create a new packet.",
    description: 'Description explaining bundle behavior',
  },
  submitButton: {
    id: 'EsignatureBundleModal.submitButton',
    defaultMessage: 'Send to {providerName}',
    description: 'Submit button label',
  },
  documentsHeader: {
    id: 'EsignatureBundleModal.documentsHeader',
    defaultMessage: 'Documents',
    description: 'Header for documents list',
  },
  loadDocumentsError: {
    id: 'EsignatureBundleModal.loadDocumentsError',
    defaultMessage: 'Failed to load documents.',
    description: 'Error message when loading documents fails',
  },
});

interface Props {
  isVisible: boolean;
  providerType: EsignatureType;
  tickets: SelectedTicket[];
  onClose: () => void;
}

export const EsignatureBundleModal = ({
  isVisible,
  providerType,
  tickets,
  onClose,
}: Props) => {
  const intl = useIntl();
  const { toast: errorToast } = useErrorToast();

  const ticketIds = useMemo(() => tickets.map((t) => t.ticketId), [tickets]);
  const [orderedTicketIds, setOrderedTicketIds] = useState<string[]>(ticketIds);
  const [prevTicketIds, setPrevTicketIds] = useState(ticketIds);

  if (ticketIds !== prevTicketIds) {
    setPrevTicketIds(ticketIds);
    setOrderedTicketIds(ticketIds);
  }

  const [
    validateBundle,
    {
      data: validationResult,
      isLoading: isValidating,
      isError: isValidationError,
    },
  ] = api.endpoints.validateMultiTicketBundle.useMutation();

  const [createBundle, { isLoading: isSubmitting }] =
    api.endpoints.createMultiTicketBundle.useMutation();

  const [getTicketDocument] = api.endpoints.getTicketDocument.useLazyQuery();
  const [documents, setDocuments] = useState<Record<string, TicketDocument>>(
    {},
  );
  const [isLoadingDocuments, setIsLoadingDocuments] = useState(false);

  const loadDocuments = useCallback(async () => {
    if (ticketIds.length === 0) return;

    setIsLoadingDocuments(true);
    try {
      const results = await Promise.all(
        ticketIds.map((ticketId) => getTicketDocument({ ticketId }).unwrap()),
      );

      const documentsMap: Record<string, TicketDocument> = {};
      ticketIds.forEach((ticketId, index) => {
        if (results[index]) {
          documentsMap[ticketId] = results[index];
        }
      });
      setDocuments(documentsMap);
    } catch {
      errorToast(new Error(intl.formatMessage(messages.loadDocumentsError)), {
        autoDismiss: false,
      });
    } finally {
      setIsLoadingDocuments(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketIds, getTicketDocument]);

  useEffect(() => {
    if (isVisible && ticketIds.length > 0) {
      loadDocuments();
    }
  }, [isVisible, ticketIds, loadDocuments]);

  const orderedTicketIdsKey = useMemo(
    () => [...orderedTicketIds].sort().join(','),
    [orderedTicketIds],
  );

  useEffect(() => {
    if (isVisible && orderedTicketIds.length > 0) {
      validateBundle({ ticket_ids: orderedTicketIds, vendor: providerType });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible, orderedTicketIdsKey, providerType, validateBundle]);

  const providerName = getEsignatureIntegrations()[providerType].name;

  const validationErrors = useMemo(() => {
    const map: Record<string, string | undefined> = {};
    validationResult?.tickets?.forEach((t) => {
      if (!t.is_valid) {
        map[t.ticket_id] = t.message;
      }
    });
    return map;
  }, [validationResult]);

  const isValid = !isValidationError && (validationResult?.is_valid ?? true);
  const bundleError = validationResult?.message;

  const handleSubmit = useCallback(async () => {
    const newTab = window.open('', '_blank');
    try {
      const result = await createBundle({
        ticket_ids: orderedTicketIds,
        vendor: providerType,
        return_url: window.location.href,
      });

      if ('data' in result && result.data && newTab) {
        newTab.location.href = result.data.links.sender_view;
        onClose();
      } else {
        newTab?.close();
      }
    } catch {
      newTab?.close();
    }
  }, [createBundle, orderedTicketIds, providerType, onClose]);

  const MIN_TICKETS = 2;

  const handleRemoveTicket = useCallback((ticketId: string) => {
    setOrderedTicketIds((prev) => prev.filter((id) => id !== ticketId));
    setDocuments((prev) => {
      const { [ticketId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const isLoading = isValidating || isLoadingDocuments;

  return (
    <Modal
      isVisible={isVisible}
      title={intl.formatMessage(messages.title, { providerName })}
      primaryAction={{
        text: intl.formatMessage(messages.submitButton, { providerName }),
        disabled: !isValid || isLoading || isSubmitting,
        isLoading: isSubmitting,
        onClick: handleSubmit,
      }}
      onCancel={onClose}
      onHide={onClose}
    >
      <Layout direction="column" spacing={4}>
        {bundleError && !isLoading && (
          <StatusMessage status="danger" message={bundleError} />
        )}

        <Text>{intl.formatMessage(messages.description)}</Text>

        <Layout direction="column" spacing={2}>
          <Text variant="body-bold">
            {intl.formatMessage(messages.documentsHeader)}
          </Text>

          {isLoading ? (
            <Layout direction="column" spacing={2}>
              {orderedTicketIds.map((ticketId) => (
                <LoadingShimmer key={ticketId} height="40px" />
              ))}
            </Layout>
          ) : (
            <SortableDocumentList
              documents={documents}
              orderedIds={orderedTicketIds}
              validationErrors={validationErrors}
              onReorder={setOrderedTicketIds}
              onRemove={handleRemoveTicket}
              canRemove={orderedTicketIds.length > MIN_TICKETS}
            />
          )}
        </Layout>
      </Layout>
    </Modal>
  );
};
